<template>
    <section class="flex flex-col gap-5">
        <header class="flex flex-col gap-2">
            <BaseButton theme="tertiary" size="sm" class="self-start" @click="emit('back')">
                <i class="bi bi-chevron-left" aria-hidden="true"></i> {{ t("reflections.admin.drafts.backToAdmin") }}
            </BaseButton>
            <div class="flex items-center gap-3 flex-wrap">
                <h2 ref="headingEl" tabindex="-1" class="title-subsection text-ink outline-none">{{ t("reflections.admin.drafts.title") }}</h2>
                <BaseButton theme="primary" size="sm" class="ml-auto" :disabled="templates.length === 0" @click="emit('new-campaign', templates)">
                    <i class="bi bi-plus-lg" aria-hidden="true"></i>
                    {{ t("reflections.admin.tab.newCampaignButton") }}
                </BaseButton>
            </div>
            <p class="body-small text-ink-muted">{{ t("reflections.admin.drafts.subtitle") }}</p>
        </header>

        <HorizontalDivider as="hr" spacing="none" />

        <div v-if="loading" class="body-small text-ink-muted">{{ t("reflectionsAdmin.panel.loading") }}</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />

        <div v-else class="flex flex-col gap-2">
            <div class="scaffold-details-small text-ink-muted">{{ t("reflections.admin.drafts.count", { count: drafts.length }) }}</div>
            <BaseCard v-if="drafts.length === 0" padding="md" class="body-small text-ink-muted"> {{ t("reflections.admin.drafts.empty") }} </BaseCard>
            <AdminDraftRow
                v-for="campaign in drafts"
                :key="campaign.id"
                :campaign="campaign"
                :busy="busyId === campaign.id"
                @delete="askDelete"
                @launch="askLaunch"
                @edit="onEdit" />
        </div>

        <BaseNotification v-if="actionStatus" :theme="actionStatus.theme" :text="actionStatus.text" @dismiss="actionStatus = null" />

        <ConfirmationDialog
            v-model:open="confirmOpen"
            :title="confirmCopy.title"
            :text="confirmCopy.text"
            :confirm-label="confirmCopy.confirmLabel"
            :confirm-theme="confirmCopy.confirmTheme"
            :close-on-confirm="false"
            @confirm="runPendingAction" />
    </section>
</template>

<script setup lang="ts">
import { extractApiErrorMessage, useFetch } from "~vue/api";
import type { AdminCampaignResponse, AdminCampaignSummary, AdminHubPayload, TemplateSummary } from "~vue/api/generated/reflections-admin";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import AdminDraftRow from "~vue/pages/reflections/AdminDraftRow.vue";
import { computed, nextTick, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

type PendingAction = { kind: "launch" | "delete"; campaign: AdminCampaignSummary };

const { t } = useI18n();

const emit = defineEmits<{
    (event: "back"): void;
    (event: "new-campaign", templates: TemplateSummary[]): void;
    (event: "edit", campaign: AdminCampaignSummary, templates: TemplateSummary[]): void;
}>();

const campaigns = ref<AdminCampaignSummary[]>([]);
const templates = ref<TemplateSummary[]>([]);
const loading = ref(true);
const loadError = ref<string | null>(null);
const busyId = ref<string | null>(null);
const actionStatus = ref<{ theme: "success" | "error"; text: string } | null>(null);
const pendingAction = ref<PendingAction | null>(null);
const headingEl = ref<HTMLElement | null>(null);

const drafts = computed(() => campaigns.value.filter((c) => c.state === "draft"));

const confirmOpen = computed({
    get: () => pendingAction.value !== null,
    set: (open: boolean) => {
        if (!open) {
            pendingAction.value = null;
        }
    },
});

const confirmCopy = computed(() => {
    const action = pendingAction.value;
    if (action?.kind === "delete") {
        return {
            title: t("reflections.admin.drafts.deleteTitle"),
            text: t("reflections.admin.drafts.deleteText", { name: action.campaign.name }),
            confirmLabel: t("common.delete"),
            confirmTheme: "danger" as const,
        };
    }
    return {
        title: t("reflections.admin.drafts.launchTitle"),
        text: action ? t("reflections.admin.drafts.launchText", { name: action.campaign.name }) : "",
        confirmLabel: t("reflections.admin.drafts.launch"),
        confirmTheme: "primary" as const,
    };
});

// Actions on different rows can overlap, and an older list response landing
// after a newer one would bring a deleted draft back. Only the latest counts.
let latestLoad = 0;

async function loadHub() {
    const thisLoad = ++latestLoad;
    const { data, error: fetchError } = await useFetch("/v1/reflections/admin/campaigns").json<AdminHubPayload>();
    if (thisLoad !== latestLoad) {
        return;
    }
    if (fetchError.value) {
        loadError.value = fetchError.value.message || t("reflections.admin.drafts.loadFailed");
        return;
    }
    loadError.value = null;
    campaigns.value = data.value?.campaigns ?? [];
    templates.value = data.value?.templates ?? [];
}

onMounted(async () => {
    await loadHub();
    loading.value = false;
});

// Launch and Delete remove the row holding the control that opened the
// dialog, so keyboard focus needs a stable place to land afterwards.
async function reloadAndRefocus() {
    await loadHub();
    await nextTick();
    headingEl.value?.focus();
}

function onEdit(campaign: AdminCampaignSummary) {
    emit("edit", campaign, templates.value);
}

function askLaunch(campaign: AdminCampaignSummary) {
    pendingAction.value = { kind: "launch", campaign };
}

function askDelete(campaign: AdminCampaignSummary) {
    pendingAction.value = { kind: "delete", campaign };
}

// The dialog must not close itself on confirm: closing runs the confirmOpen
// setter, which would clear the pending action before it is read here.
async function runPendingAction() {
    const action = pendingAction.value;
    pendingAction.value = null;
    if (!action) {
        return;
    }
    if (action.kind === "launch") {
        await launchCampaign(action.campaign);
    } else {
        await deleteCampaign(action.campaign);
    }
}

// The row stays busy until the list has reloaded, so a second click can't
// hit a campaign that has already been launched or deleted.
async function launchCampaign(campaign: AdminCampaignSummary) {
    busyId.value = campaign.id;
    actionStatus.value = null;
    try {
        const { data, error: fetchError } = await useFetch(`/v1/reflections/admin/campaigns/${campaign.id}/launch`, { method: "POST" }).json<AdminCampaignResponse>();
        if (fetchError.value) {
            actionStatus.value = { theme: "error", text: (data.value as { error?: string } | null)?.error || t("reflections.admin.drafts.launchFailed") };
            return;
        }
        actionStatus.value = { theme: "success", text: t("reflections.admin.drafts.launched", { name: campaign.name }) };
        await reloadAndRefocus();
    } finally {
        busyId.value = null;
    }
}

function parseErrorBody(body: string | null): unknown {
    try {
        return body ? JSON.parse(body) : null;
    } catch {
        return null;
    }
}

async function deleteCampaign(campaign: AdminCampaignSummary) {
    busyId.value = campaign.id;
    actionStatus.value = null;
    try {
        // A successful delete is a 204 with no body, so it can't be read as JSON.
        const { data, error: fetchError, statusCode } = await useFetch(`/v1/reflections/admin/campaigns/${campaign.id}`, { method: "DELETE" }).text();
        if (fetchError.value || (statusCode.value ?? 0) >= 400) {
            actionStatus.value = { theme: "error", text: extractApiErrorMessage(parseErrorBody(data.value), t("reflections.admin.drafts.deleteFailed")) };
            return;
        }
        actionStatus.value = { theme: "success", text: t("reflections.admin.drafts.deleted", { name: campaign.name }) };
        await reloadAndRefocus();
    } finally {
        busyId.value = null;
    }
}
</script>
